/**
 *
 * This component will render EventActions to perform RSVP / Cancel RSVP to event.
 * @module views/components/EventActions
 * @memberof -Common.
 */
import './EventActions.scss';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import Icon from '@ulta/core/components/Icon/Icon';
import InlineMessage from '@ulta/core/components/InlineMessage/InlineMessage';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Markdown from '@ulta/core/components/Markdown/Markdown';
import SelectField from '@ulta/core/components/SelectField_New/SelectField_New';
import Text from '@ulta/core/components/Text/Text';
import { useAriaLiveRegion } from '@ulta/core/providers/AriaLiveRegionProvider/AriaLiveRegionProvider';
import { useUserContext } from '@ulta/core/providers/UserContextProvider/UserContextProvider';
import { hasItems } from '@ulta/core/utils/array/array';
import { CLIENT_ACTION_TYPES } from '@ulta/core/utils/constants/action';
import { isServer } from '@ulta/core/utils/device_detection/device_detection';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';
import { setStorage } from '@ulta/core/utils/storage/storage';
import { STORAGE_KEY } from '@ulta/core/utils/storageKeys/storageKeys';

import Tag from '@ulta/modules/Tag/Tag';

import CancelRsvpConfirmation from '@ulta/components/CancelRsvpConfirmation/CancelRsvpConfirmation';

import { moveScreenReaderFocus } from '@ulta/utils/accessibility/accessibility';
import { EVENT_STYLES } from '@ulta/utils/constants/events';
import { composePostLoginRSVP } from '@ulta/utils/event/event';

import * as utils from './EventActions';

/**
 * Represents a EventActions component
 *
 * @method
 * @param {EventActionsProps} props - React properties passed from composition
 * @returns EventActions
 */
const EventActions = function( props ){
  const {
    cancelConfirmationDialog,
    cancelRsvpAction,
    duration,
    eventData,
    eventId,
    eventSchedule,
    eventStyle,
    getDirectionLink,
    id,
    informationText,
    inlineMessage,
    invokeMutation,
    isUserRsvpd,
    loading,
    rsvpAction,
    rsvpActionDisabled,
    rsvpConfirmationText,
    rsvpdLabel,
    signInAction,
    statusTag,
    timeSlots
  } = props;

  const buttonRef = useRef( null );
  const userInteractedWithButton = useRef( false );

  // Initialize with the first enabled time slot, or the preselected value (passed in from DXL)
  const firstEnabledTimeSlotValue = useMemo(
    () => {
      if( !timeSlots?.value ){
        return timeSlots?.options?.find( option => option.enabled )?.value;
      }
      return timeSlots.value;
    },
    [timeSlots?.options]
  );
  const [showAnimation, setShowAnimation] = useState( false );
  const [inlineMessageProps, setInlineMessageProps] = useState();
  const [selectedTimeSlot, setSelectedTimeSlot] = useState( firstEnabledTimeSlotValue );
  const { user } = useUserContext();
  const { broadcastMessage } = useAriaLiveRegion();

  /**
   * Handles change of dropdown time slot value
   */
  const handleOnChange = useCallback(
    utils.composeHandleOnChange(
      { setSelectedTimeSlot }
    ),
    [setSelectedTimeSlot]
  );

  const isTimeSlotEvent = eventStyle === EVENT_STYLES?.TIMESLOT;
  const timeSlot = isTimeSlotEvent ? selectedTimeSlot : null;

  const hasCheckedPostLoginStatus = useRef( false );

  /**
  * ADA -> Broadcasts inline message modifications for screen readers
  */
  const a11yHandler = useCallback(
    utils.composeA11yResponse( { message: inlineMessageProps?.message }, { broadcastMessage } ),
    [inlineMessageProps, broadcastMessage]
  );

  useEffect( a11yHandler, [a11yHandler] );

  useEffect(
    utils.handleInlineMessage(
      { isUserRsvpd, inlineMessage },
      { setShowAnimation, setInlineMessageProps }
    ),
    [isUserRsvpd, inlineMessage] );

  // When the isUserRsvpd status changes, move screen reader focus to the action button.
  useEffect( () => {
    if( buttonRef.current && userInteractedWithButton.current ){
      // When the component mounts with this flag, move the screen reader's focus
      moveScreenReaderFocus( buttonRef.current );
      userInteractedWithButton.current = false;
    }
  }, [isUserRsvpd, buttonRef.current] );

  useEffect(
    composePostLoginRSVP(
      { loading, loginStatus: user?.loginStatus, hasCheckedPostLoginStatus },
      { invokeMutation }
    ),
    [loading, hasCheckedPostLoginStatus, user?.loginStatus] );

  const shouldShowRSVPAction = Boolean( !isUserRsvpd && rsvpAction?.label );
  const shouldShowCancelRSVPAction = Boolean( !shouldShowRSVPAction && cancelRsvpAction?.label );
  const shouldShowAnimation = Boolean( !isUserRsvpd && showAnimation );


  const handleOnClickRsvpButton = useCallback(
    utils.composeOnClickRsvpButton(
      {
        eventId,
        rsvpAction,
        cancelRsvpAction,
        signInAction,
        user,
        loginStatus: user?.loginStatus,
        eventData,
        eventStyle,
        timeSlot
      },
      {
        invokeMutation, setShowAnimation, setInlineMessageProps
      }
    ), [eventData, eventStyle, timeSlot, user, cancelRsvpAction, rsvpAction, signInAction, eventId, selectedTimeSlot] );

  const shouldRenderInformationalTexts = eventSchedule?.text || rsvpConfirmationText?.text || informationText?.text;
  const showTimeSlotSelectField = hasItems( timeSlots?.options );

  const actionButtonProps = useMemo( () => utils.getEventActionButtonProps(
    {
      cancelConfirmationDialog,
      cancelRsvpAction,
      rsvpActionDisabled,
      eventId,
      eventStyle,
      invokeMutation,
      loading,
      rsvpAction,
      isUserRsvpd,
      timeSlot,
      signInAction,
      loginStatus: user?.loginStatus,
      userInteractedWithButton
    },
    {
      handleOnClickRsvpButton: () => {
        userInteractedWithButton.current = true;
        handleOnClickRsvpButton();
      }
    }
  ), [
    cancelConfirmationDialog,
    cancelRsvpAction,
    rsvpActionDisabled,
    eventId,
    handleOnClickRsvpButton,
    eventStyle,
    invokeMutation,
    isUserRsvpd,
    loading,
    rsvpAction,
    signInAction,
    timeSlot,
    user,
    userInteractedWithButton
  ] );

  return (
    <div className='EventActions'>
      { shouldRenderInformationalTexts && (
        <div
          className='EventActions__InformationalTexts'
          data-testid='informational-texts'
        >
          { eventSchedule?.text && (
            <Text
              htmlTag='p'
              textStyle='body'
            >
              { eventSchedule?.text }
            </Text>
          ) }
          { rsvpConfirmationText?.text && (
            <Text
              htmlTag='p'
              textStyle='body'
              color='validate-600'
            >
              { rsvpConfirmationText?.text }
            </Text>
          ) }
          { informationText?.text && (
            <Text
              htmlTag='p'
              textStyle='body'
            >
              { informationText?.text }
            </Text>
          )
          }
        </div>
      ) }
      { isTimeSlotEvent && (
        <div className={ classNames( 'EventActions__TimeSlotContainer', {
          'EventActions__TimeSlotContainer--reserved': !showTimeSlotSelectField
        } ) }
        >
          { duration && (
            <Markdown
              bodyStyle={ 'body-2-noPadding' }
              cmsMarkdownContent={ duration }
            />
          ) }
          { showTimeSlotSelectField && (
            <Formik
              initialValues={ { [timeSlots?.name]: selectedTimeSlot } }
            >
              { () => (
                <Form className='EventActions__TimeSlotForm'>
                  <div className='EventActions__TimeSlotSelectFieldContainer'>
                    <SelectField
                      options={ timeSlots.options }
                      enabled
                      name={ timeSlots.name }
                      value={ selectedTimeSlot }
                      onChange={ handleOnChange }
                      label='Time Slot'
                      hideLabel={ true }
                      uniqueId='timeSlotDropdown'
                    />
                    { timeSlots.helpText && (
                      <Text htmlTag='p'
                        textStyle='body-sm'
                        color='neutral-600'
                        fontWeight='500'
                        className='EventActions__TimeSlotSelectFieldHelpText'
                      >
                        { timeSlots.helpText }
                      </Text>
                    ) }
                  </div>
                </Form>
              ) }
            </Formik>
          ) }
          { statusTag && <Tag { ...statusTag } /> }
        </div>
      ) }
      <div
        className='EventActions__buttonContainer'
        aria-live='polite'
        role='status'
      >
        <div
          className={ classNames( {
            [`EventActions__cancelRSVPButtonContainer`]: shouldShowCancelRSVPAction,
            [`EventActions__container`]: shouldShowRSVPAction
          } ) }
        >
          { shouldShowAnimation && (
            <div className='EventActions__AddedProgress'>
              <div className='EventActions__SvgContainer'>
                <Text htmlTag='span'>
                  <Icon className={ classNames( 'EventActions__Check' ) }
                    aria-hidden={ true }
                    size={ 's' }
                    name={ 'Check' }
                    burst
                  />
                </Text>
              </div>
              <Text
                htmlTag='span'
                textStyle='body-3'
                fontWeight='500'
              >
                { rsvpdLabel }
              </Text>
            </div>
          ) }
          { !shouldShowAnimation && actionButtonProps && (
            <Button
              { ...spreadProps( { ...actionButtonProps } ) }
              ref={ buttonRef }
            />
          ) }
        </div>
        { getDirectionLink?.label && (
          <div className='EventActions__directionLink'>
            <Link_Huge
              target='_blank'
              url={ getDirectionLink.url }
              action={ getDirectionLink }
              withHover
              iconName='ArrowForward'
              iconPosition='after'
              screenReaderText={ getDirectionLink.accessibility }
            >
              { getDirectionLink.label }
            </Link_Huge>
          </div>
        ) }
      </div>
      { inlineMessageProps && (
        <div className='EventActions__messages'
          data-testid='RsvpErrorMessage'
        >
          <InlineMessage { ...inlineMessageProps } />
        </div>
      ) }
    </div>
  );
};

/**
  * A handler function for setting Inline Message
  * @method
  * @param {String} data - Passing data as an argument
  * @param {object} methods - methods object passed in as an argument
  */
export const handleInlineMessage = ( data, methods ) => () => {
  const { isUserRsvpd, inlineMessage } = handleEmptyObjects( data );
  const { setShowAnimation, setInlineMessageProps } = handleEmptyObjects( methods );

  if( !isUserRsvpd && setShowAnimation ){
    setShowAnimation( false );
  }

  if( !setInlineMessageProps ){
    return;
  }
  if( inlineMessage?.message ){
    setInlineMessageProps( {
      ...inlineMessage,
      role: inlineMessage.icon === 'error' ? 'alert' : 'status',
      messageType: inlineMessage.icon || 'error',
      shouldFocusOnMount: true
    } );
  }
  else {
    setInlineMessageProps( null );
  }
};

/**
 * @const {string} INLINEMESSAGE_ADA_EVENT - ADA broadcast id
 */
export const INLINEMESSAGE_ADA_EVENT = 'inlineMessageBroadCastEventActions';

/**
 * @const {number} INLINE_MESSAGE_ADA_INTERVAL - Broadcast to screen reader cleanup interval
 */
export const INLINE_MESSAGE_ADA_INTERVAL = 30000;

/**
 * Compose A11y response
 * @param {object} data - data object passed in as an argument
 * @param {string} data.message - the message to be broadcasted.
 * @param {object} methods - methods object passed in as an argument
 * @param {function} methods.broadcastMessage - function to broadcast message.
 */
export const composeA11yResponse = ( data, methods ) => () => {
  const { message } = data || {};
  const { broadcastMessage } = methods || {};

  if( !message || !broadcastMessage ){
    return;
  }

  broadcastMessage( {
    message,
    id: INLINEMESSAGE_ADA_EVENT,
    delay: INLINE_MESSAGE_ADA_INTERVAL
  } );
};


/**
  * A click handler function for rsvp
  * @method
  * @param {Object} methods - Passing methods as an argument for the mutation call
  * @param {String} data - Passing data as an argument
  */

export const composeOnClickRsvpButton = ( data, methods ) => ( ) => {
  const { eventId, rsvpAction, signInAction, loginStatus, eventData, eventStyle, timeSlot } = handleEmptyObjects( data );
  const { invokeMutation, setShowAnimation, setInlineMessageProps } = handleEmptyObjects( methods );

  const action = loginStatus ? rsvpAction : signInAction;

  const dispatchLogin = signInAction?.clientActionType === CLIENT_ACTION_TYPES.AUTH_SIGNIN;
  const timeSlotProps = eventStyle === EVENT_STYLES?.TIMESLOT ? { eventStyle, timeSlot } : {};

  if( !loginStatus && dispatchLogin ){
    setStorage( {
      secure: true,
      key: STORAGE_KEY.postLoginRsvpDetails,
      value: {
        eventId,
        eventData,
        rsvpActionGraphql: rsvpAction.graphql,
        rsvpCustomHeaders: rsvpAction.customHeaders,
        rsvpParams: rsvpAction.params,
        eventStyle,
        timeSlot
      }
    } );
    return;
  }

  if( !action?.graphql || !invokeMutation ){
    return;
  }
  setInlineMessageProps( null );
  setShowAnimation( true );


  /**
      * Call event RSVP mutation
      */
  invokeMutation( {
    graphql: action.graphql,
    customHeaders: action.customHeaders,
    params: action.params,
    variables: {
      moduleParams: {
        eventId,
        eventData,
        ...timeSlotProps
      },
      url: { path: !isServer() && global.location.href }
    }
  } );
};

/**
 * A handler function for getting action button props for the RSVP or Cancel RSVP button.
 * @method
 * @param {object} data - The data object.
 * @param {object} data.cancelConfirmationDialog - The cancel confirmation dialog props.
 * @param {object} data.cancelRsvpAction - The cancel RSVP action props.
 * @param {boolean} data.rsvpActionDisabled - Flag to disable RSVP button when user has not RSVP'd.
 * @param {string} data.eventId - The event ID.
 * @param {string} data.eventStyle - The event style.
 * @param {Function} data.invokeMutation - The function to invoke a mutation.
 * @param {boolean} data.loading - The loading state.
 * @param {object} data.rsvpAction - The RSVP action props.
 * @param {boolean} data.isUserRsvpd - Flag indicating if user has RSVP'd.
 * @param {string} data.timeSlot - The selected time slot.
 * @param {boolean} data.loginStatus - The user's login status.
 * @param {object} data.signInAction - The sign-in action props.
 * @param {object} methods - The methods object.
 * @param {Function} methods.handleOnClickRsvpButton - The click handler for the RSVP button.
 * @returns {object|undefined} The props for the event action button, or undefined if no button should be rendered.
 */
export const getEventActionButtonProps = ( data, methods ) => {
  const {
    cancelConfirmationDialog,
    cancelRsvpAction,
    eventId,
    eventStyle,
    invokeMutation,
    isUserRsvpd,
    loading,
    loginStatus,
    rsvpAction,
    rsvpActionDisabled,
    signInAction,
    timeSlot
  } = handleEmptyObjects( data );

  const { handleOnClickRsvpButton } = handleEmptyObjects( methods );

  // If user has not rsvp'd and there is a rsvpAction.label, return RSVP props
  if( !isUserRsvpd && rsvpAction?.label ){
    // Disable RSVP action button if the user is NOT registered and rsvpActionDisabled is true.
    const disableRsvp = Boolean( !isUserRsvpd && rsvpActionDisabled );

    return {
      label: rsvpAction.label,
      ariaLabel: rsvpAction.label,
      disabled: disableRsvp,
      action: loginStatus ? rsvpAction : signInAction,
      size:'compact',
      variant:'primary',
      onClick: handleOnClickRsvpButton
    };
  }
  // Else, if there is a cancelRsvpAction.label and the user is RSVPd, return Cancel RSVP props.
  else if( cancelRsvpAction?.label && isUserRsvpd ){
    return {
      label: cancelRsvpAction.label,
      ariaLabel: cancelRsvpAction.label,
      onClick: ( e ) => {
        handleOnClickRsvpButton();
        e.stopPropagation();
        e.preventDefault();
      },
      size:'compact',
      variant:'secondary',
      className:'EventCard__cancelRsvpButton',
      action: {
        ...cancelRsvpAction,
        navigationType: 'modal'
      },
      overlayContent: (
        <CancelRsvpConfirmation
          headerText={ cancelConfirmationDialog?.title }
          confirmLabel={ cancelConfirmationDialog?.confirmLabel }
          confirmMessage={ cancelConfirmationDialog?.message }
          cancelLabel={ cancelConfirmationDialog?.cancelLabel }
          action={ cancelRsvpAction }
          invokeMutation={ invokeMutation }
          eventId={ eventId }
          loading={ loading }
          eventStyle={ eventStyle }
          timeSlot={ timeSlot }
        />
      ),
      overlayCustomClass: 'EventDetailCard',
      overlayAriaLabel: cancelConfirmationDialog?.title
    };
  }
};

/**
 * composeHandleOnChange method to handle setting selected timeslot on change of dropdown value
 * @param {object} methods - Methods
 */
export const composeHandleOnChange = ( methods ) => ( value ) => {
  const { setSelectedTimeSlot } = handleEmptyObjects( methods );
  setSelectedTimeSlot( value );
};

/**
 * Property type definitions
 * @typedef EventActionsProps
 * @type {object}
 * @property {string} id - sets the content ID for the EventAction
 * @property {object} rsvpAction - Sets the label for RSVP button and has graphql action.
 * @property {object} cancelRsvpAction - Sets the label for Cancel button and has graphql action.
 * @property {object} signInAction - Sets the signInAction property
 * @property {string} eventData - Sets the event data for RSVP action.
 * @property {boolean} isUserRsvpd - Sets the event rsvp status for user.
 * @property {rsvpdLabel} body2 - Sets the lable for animated button on RSVP click action.
 * @property {string} [loading] - The loading state of the component (optional).
 * @property {function} invokeMutation - invokes graphql calls action.
 * @property {object} getDirectionLink - Sets the label for get direction label and  provide link to navigate to map.
 * @property {object} inlineMessage - Sets the error message to RSVP action.
 * @property {Array<object>} timeSlots - Array of time slot objects for the event.
 * @property {string} timeSlotHelpText - Help text for the time slot dropdown.
 * @property {string} eventFullMessage - Message to display when the event is full.
 * @property {number} urgencyTagThreshold - Threshold to show the urgency tag.
 *
 */

export const propTypes = {
  cancelConfirmationDialog: PropTypes.shape( {
    cancelLabel: PropTypes.string,
    confirmLabel: PropTypes.string,
    message: PropTypes.string,
    title: PropTypes.string
  } ),
  cancelRsvpAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  componentKey: PropTypes.string,
  eventData: PropTypes.object,
  eventId: PropTypes.string,
  getDirectionLink: PropTypes.object,
  id: PropTypes.string,
  isUserRsvpd: PropTypes.bool,
  rsvpAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  rsvpdLabel: PropTypes.string,
  signInAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  inlineMessage: PropTypes.shape( {
    icon: PropTypes.oneOf( ['warning', 'error', 'success', 'info', 'infocircle'] ),
    message: PropTypes.string
  } ),
  timeSlots: PropTypes.arrayOf( PropTypes.shape( {
    startTime: PropTypes.string,
    spotsAvailable: PropTypes.number
  } ) ),
  timeSlotHelpText: PropTypes.string,
  eventFullMessage: PropTypes.string,
  urgencyTagThreshold: PropTypes.number
};


EventActions.propTypes = propTypes;

export default EventActions;